import { difference, uniq } from 'lodash-es';
import { FC, JSX } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import Input from '@semcore/ui/input';

import type { Content } from '@/entities/content/api/types';
import { useGetAdUnitsSearchQuery } from '@/entities/sites/api/sitesApi';
import PageFilter from '@/features/page-filter';

import { PublishURLs } from './PublishURLs/PublishURLs';

interface IMetadataSettingsProps {}

const MetadataSettings: FC<IMetadataSettingsProps> = (): JSX.Element => {
  const { control, watch, setValue, getValues } = useFormContext<Content>();
  const sitesSelected: number[] = watch('sites');
  const publicationSelected: number = watch('publication');

  const getAdUnitsSearchQuery = useGetAdUnitsSearchQuery();

  // should go before onChange because it uses watch('sites')
  const handleSitesChange = async (value: number[]) => {
    if (!getAdUnitsSearchQuery.data) return;

    const selectedSites = difference(value, sitesSelected);
    const deselectedSites = difference(sitesSelected, value);

    const currentAdUnits: number[] = getValues('zones') || [];

    if (selectedSites.length > 0) {
      const adUnitsToAdd = getAdUnitsSearchQuery.data
        .filter((adUnit) => selectedSites.includes(adUnit.site_id))
        .map((adUnit) => adUnit.id);

      setValue('zones', uniq([...currentAdUnits, ...adUnitsToAdd]));
    }

    if (deselectedSites.length > 0) {
      const adUnitsToRemove = getAdUnitsSearchQuery.data
        .filter((adUnit) => deselectedSites.includes(adUnit.site_id))
        .map((adUnit) => adUnit.id);

      setValue('zones', difference(currentAdUnits, adUnitsToRemove));
    }
  };

  return (
    <div className="flex gap-4">
      <div className="flex w-96 flex-col gap-2">
        <Controller
          control={control}
          name="name"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <Input size="m" state={error ? 'invalid' : 'normal'}>
                <Input.Value
                  placeholder="Name"
                  value={value}
                  onChange={onChange}
                />
              </Input>
              {error && (
                <div className="text-sm font-light text-red">
                  {error?.message}
                </div>
              )}
            </>
          )}
        />
      </div>

      <div className="flex w-44 flex-col gap-2">
        <Controller
          control={control}
          name="sites"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <PageFilter.SiteSelect
                value={value}
                onChange={(value) => {
                  onChange(value);
                  handleSitesChange(value);
                }}
                className="!min-w-[100px]"
              />
              {error && (
                <div className="text-sm font-light text-red">
                  {error?.message}
                </div>
              )}
            </>
          )}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Controller
          control={control}
          name="publication"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <PageFilter.ContentPublicationSelect
                value={value}
                onChange={onChange}
                disabled={!sitesSelected.length}
              />
              {error && (
                <div className="text-sm font-light text-red">
                  {error?.message}
                </div>
              )}
            </>
          )}
        />
      </div>

      <div
        className={twMerge(
          'flex w-44 flex-col gap-2',
          publicationSelected <= 0 && 'hidden'
        )}
      >
        <Controller
          control={control}
          name="publication_urls"
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => <PublishURLs value={value} onChange={onChange} error={error} />}
        />
      </div>

      <div className="flex w-52 flex-col gap-2">
        <Controller
          control={control}
          name="zones"
          render={({
            field: { value = [], onChange },
            fieldState: { error },
          }) => (
            <>
              <PageFilter.AdUnitsSelect
                sitesSelected={sitesSelected}
                value={value}
                disabled={!sitesSelected.length}
                onChange={onChange}
              />
              {error && (
                <div className="text-sm font-light text-red">
                  {error?.message}
                </div>
              )}
            </>
          )}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Controller
          control={control}
          name="categories"
          render={({
            field: { value = [], onChange },
            fieldState: { error },
          }) => (
            <>
              <PageFilter.ContentCategorySelect
                value={value}
                onChange={onChange}
                disabled={!sitesSelected.length}
              />
              {error && (
                <div className="text-sm font-light text-red">
                  {error?.message}
                </div>
              )}
            </>
          )}
        />
      </div>
    </div>
  );
};

export default MetadataSettings;
